/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, MapPin, Calendar, ChevronRight } from 'lucide-react';
import TecnicoLayout from '../components/TecnicoLayout';
import { interventiAPI } from '../api/interventi';
import { fmtDataOra } from '../utils/date';

const ACCENT = '#10B981';
const STATO_LABELS = { in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato' };
const PRIO = { urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' }, alta: { bg: '#FED7AA', color: '#9A3412', label: 'Alta' }, normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' }, bassa: { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' } };
const CATEGORIE_INTERNE = { magazzino: 'Magazzino', manutenzione_automezzi: 'Manutenzione automezzi', formazione: 'Formazione', riunione: 'Riunione', altro: 'Altro' };

export default function Interventi() {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('attivi');

  const { data: result = { data: [] }, isLoading } = useQuery({
    queryKey: ['interventi-tecnico', filtro],
    queryFn: () => interventiAPI.list({
      stato: filtro === 'completati' ? 'completato' : undefined,
      mine: true,
    }),
  });

  // Filtra lato client: attivi = non completati/annullati
  const lista = (result.data || []).filter((i) => {
    if (filtro === 'attivi') return !['completato', 'annullato'].includes(i.stato);
    if (filtro === 'completati') return i.stato === 'completato';
    return true;
  });

  return (
    <TecnicoLayout title="I miei lavori">
      <div style={styles.filters}>
        {[
          { v: 'attivi', l: 'Attivi' },
          { v: 'completati', l: 'Completati' },
          { v: 'tutti', l: 'Tutti' },
        ].map((f) => (
          <button key={f.v} onClick={() => setFiltro(f.v)} style={{ ...styles.filterBtn, ...(filtro === f.v ? styles.filterActive : {}) }}>
            {f.l}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={styles.loading}>Caricamento...</div>
      ) : lista.length === 0 ? (
        <div style={styles.empty}>
          <ClipboardList size={40} color="#9CA3AF" />
          <p>Nessun lavoro {filtro === 'attivi' ? 'attivo' : filtro === 'completati' ? 'completato' : ''}.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {lista.map((i) => {
            const p = PRIO[i.priorita] || PRIO.normale;
            return (
              <div key={i.id} style={styles.card} onClick={() => navigate(`/interventi/${i.id}`)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.cardHead}>
                    <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>{STATO_LABELS[i.stato]}</span>
                    <span style={{ ...styles.prioBadge, backgroundColor: p.bg, color: p.color }}>{p.label}</span>
                    <code style={styles.code}>{i.codice}</code>
                  </div>
                  <div style={{ fontWeight: 600, marginTop: '0.3rem' }}>{i.titolo}</div>
                  <div style={styles.meta}>
                    {i.cliente_id ? (
                      <>{i.cliente_nome} {i.cliente_cognome}</>
                    ) : (
                      <span style={styles.internoBadge}>Lavoro interno{i.categoria_interna ? ` · ${CATEGORIE_INTERNE[i.categoria_interna] || i.categoria_interna}` : ''}</span>
                    )}
                    {i.citta && <> · <MapPin size={12} /> {i.citta}</>}
                  </div>
                  {i.data_appuntamento && <div style={styles.meta}><Calendar size={12} /> {fmtDataOra(i.data_appuntamento)}</div>}
                </div>
                <ChevronRight size={18} color="#9CA3AF" />
              </div>
            );
          })}
        </div>
      )}
    </TecnicoLayout>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  filters: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' },
  filterBtn: { flex: 1, padding: '0.55rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500 },
  filterActive: { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  card: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700 },
  prioBadge: { padding: '0.18rem 0.5rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600 },
  code: { backgroundColor: '#E5E7EB', padding: '0.12rem 0.4rem', borderRadius: '4px', fontSize: '0.74rem', fontFamily: 'ui-monospace, monospace' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: '#6B7280', marginTop: '0.3rem' },
  internoBadge: { backgroundColor: '#EDE9FE', color: '#5B21B6', padding: '0.1rem 0.45rem', borderRadius: '5px', fontWeight: 600, fontSize: '0.78rem' },
  empty: { padding: '3rem', textAlign: 'center', color: '#9CA3AF', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', borderRadius: '12px' },
};
